import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../../config/api';
import { useToast } from '../../hooks/useToast';
import {
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Chip,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon, Person as PersonIcon } from '@mui/icons-material';
import { Breadcrumbs } from '../../components/ui/Breadcrumbs';
import { Skeleton } from '../../components/ui/Skeleton';
import { Toast } from '../../components/ui/Toast';
import { Card } from '../../components/ui/Card';

interface User {
  _id: string;
  email: string;
  name: string;
  roles: string[];
  activeRole: string;
  isAdmin: boolean;
  createdAt: string;
  lastLogin: string | null;
  actif: boolean;
}

const UserDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Toast notifications
  const { toasts, removeToast, success, error: showError } = useToast();

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    roles: [] as string[],
    isAdmin: false,
    actif: false,
    password: ''
  });

  const availableRoles = ['admin', 'provider', 'partner'];

  useEffect(() => {
    if (id) {
      loadUser();
    }
  }, [id]);

  const loadUser = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('@TikodAdmin:token');
      const response = await fetch(buildApiUrl(`/api/users/${id}`), {
        headers: {
          'x-auth-token': token || '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération de l\'utilisateur');
      }

      const userData = await response.json();
      setUser(userData);
      setFormData({
        name: userData.name,
        email: userData.email,
        roles: userData.roles,
        isAdmin: userData.isAdmin,
        actif: userData.actif,
        password: ''
      });
      success('Utilisateur chargé avec succès');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const token = localStorage.getItem('@TikodAdmin:token');
      const updateData: any = {
        name: formData.name,
        email: formData.email,
        roles: formData.roles,
        isAdmin: formData.isAdmin,
        actif: formData.actif
      };

      if (formData.password) {
        updateData.password = formData.password;
      }

      const response = await fetch(buildApiUrl(`/api/users/${id}`), {
        method: 'PUT',
        headers: {
          'x-auth-token': token || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour de l\'utilisateur');
      }

      const updatedUser = await response.json();
      setUser(updatedUser);
      success('Utilisateur mis à jour avec succès');
      setFormData(prev => ({ ...prev, password: '' }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la sauvegarde';
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = (event: any) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      roles: typeof value === 'string' ? value.split(',') : value
    }));
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: { xs: 2, md: 4 }, mb: 4 }}>
        <Breadcrumbs items={[{ label: 'Utilisateurs', path: '/users' }, { label: 'Détails' }]} />
        <Card>
          <Skeleton variant="circular" width={48} height={48} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 2, borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 2, borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 2 }} />
        </Card>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ mt: { xs: 2, md: 4 }, mb: 4 }}>
        <Breadcrumbs items={[{ label: 'Utilisateurs', path: '/users' }, { label: 'Détails' }]} />
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'error.lighter' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Utilisateur non trouvé
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 2, md: 4 }, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs items={[{ label: 'Utilisateurs', path: '/users' }, { label: 'Détails' }]} />

      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 2, sm: 0 }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/users')}
          sx={{ mr: { sm: 2 }, alignSelf: { xs: 'flex-start', sm: 'auto' } }}
          size={isMobile ? 'small' : 'medium'}
        >
          Retour
        </Button>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontSize: { xs: '1.5rem', md: '2.125rem' },
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          Détails de l'utilisateur
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <PersonIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight="700">{user.name}</Typography>
            <Typography variant="body2" color="text.secondary">{user.email}</Typography>
          </Box>
          <Chip
            label={user.actif ? 'Actif' : 'Inactif'}
            size="small"
            color={user.actif ? 'success' : 'default'}
            variant={user.actif ? 'filled' : 'outlined'}
            sx={{ ml: 'auto' }}
          />
        </Box>
      </Card>

      <Card>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{
            fontSize: { xs: '1rem', sm: '1.25rem' }
          }}>
            Informations générales
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1, sm: 2 }, 
            mb: 2, 
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Chip 
              label={`ID: ${user._id}`} 
              variant="outlined" 
              size={isMobile ? 'small' : 'medium'}
            />
            <Chip 
              label={`Créé le: ${new Date(user.createdAt).toLocaleDateString('fr-FR')}`} 
              variant="outlined" 
              size={isMobile ? 'small' : 'medium'}
            />
            <Chip 
              label={`Dernière connexion: ${user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('fr-FR') : 'Jamais'}`} 
              variant="outlined" 
              size={isMobile ? 'small' : 'medium'}
            />
          </Box>
        </Box>

        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            label="Nom"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            fullWidth
            required
            size={isMobile ? 'small' : 'medium'}
          />

          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            fullWidth
            required
            size={isMobile ? 'small' : 'medium'}
          />

          <TextField
            label="Nouveau mot de passe (optionnel)"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
            fullWidth
            helperText="Laissez vide pour conserver le mot de passe actuel"
            size={isMobile ? 'small' : 'medium'}
          />

          <FormControl fullWidth>
            <InputLabel size={isMobile ? 'small' : 'medium'}>Rôles</InputLabel>
            <Select
              multiple
              value={formData.roles}
              onChange={handleRoleChange}
              label="Rôles"
              size={isMobile ? 'small' : 'medium'}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {availableRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 3 }
          }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isAdmin}
                  onChange={(e) => setFormData(prev => ({ ...prev, isAdmin: e.target.checked }))}
                  size={isMobile ? 'small' : 'medium'}
                />
              }
              label="Administrateur"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.actif}
                  onChange={(e) => setFormData(prev => ({ ...prev, actif: e.target.checked }))}
                  size={isMobile ? 'small' : 'medium'}
                />
              }
              label="Compte actif"
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>Modifier les informations</Typography>

          <TextField
            label="Nom"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            fullWidth
            required
            size={isMobile ? 'small' : 'medium'}
          />

          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            fullWidth
            required
            size={isMobile ? 'small' : 'medium'}
          />

          <TextField
            label="Nouveau mot de passe (optionnel)"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
            fullWidth
            helperText="Laissez vide pour conserver le mot de passe actuel"
            size={isMobile ? 'small' : 'medium'}
          />

          <FormControl fullWidth>
            <InputLabel size={isMobile ? 'small' : 'medium'}>Rôles</InputLabel>
            <Select
              multiple
              value={formData.roles}
              onChange={handleRoleChange}
              label="Rôles"
              size={isMobile ? 'small' : 'medium'}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {availableRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 3 }
          }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isAdmin}
                  onChange={(e) => setFormData(prev => ({ ...prev, isAdmin: e.target.checked }))}
                  size={isMobile ? 'small' : 'medium'}
                />
              }
              label="Administrateur"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.actif}
                  onChange={(e) => setFormData(prev => ({ ...prev, actif: e.target.checked }))}
                  size={isMobile ? 'small' : 'medium'}
                />
              }
              label="Compte actif"
            />
          </Box>

          <Box sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/users')}
              disabled={saving}
              fullWidth={isMobile}
              size={isMobile ? 'large' : 'medium'}
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              fullWidth={isMobile}
              size={isMobile ? 'large' : 'medium'}
              sx={{
                background: 'linear-gradient(135deg, #129baf 0%, #0e7989 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0e7989 0%, #096670 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 20px rgba(18, 155, 175, 0.4)',
                },
              }}
            >
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </Box>
        </Box>
      </Card>

      {/* Toast notifications */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          open={true}
          onClose={() => removeToast(toast.id)}
          message={toast.message}
          severity={toast.severity}
        />
      ))}
    </Container>
  );
};

export default UserDetails; 